// Shared anchor/open/close lifecycle for Portal-mounted dropdowns +
// popovers (SearchableSelect, Combobox, ColumnHeader, Dropdown).
// Owns:
//   - open/closed signal
//   - fixed-positioning rect anchored to the trigger element
//   - outside-mousedown → close
//   - scroll/resize → reposition
//   - optional Escape → close
//
// Consumers ref-bind the trigger + popup elements via bindTrigger /
// bindPopup and reach into pos() to lay the Portal node out.

import { createSignal, onCleanup, onMount, type Accessor } from "solid-js";
import { flipPopupPositionForElement } from "../inputs/flip-popup-position";

export interface PopupAnchorPos {
  top: number;
  left: number;
  width: number;
}

export interface UsePopupAnchorOptions {
  // Width override for the popup. Default = the trigger's measured width
  // (so the popup matches an input field). Use this when the popup wants
  // a fixed minimum unrelated to the trigger (e.g. the column-filter
  // popover is wider than its small caret trigger).
  width?: number;
  // When true, document-level Escape closes the popup. Off by default —
  // input-based widgets handle Escape from their input's onKeyDown so a
  // document listener would double-fire.
  closeOnEscape?: boolean;
  // Worst-case popup height used to pick the flip direction BEFORE the
  // popup mounts (so the first paint already lands at the right spot).
  // Defaults to 420 — the .popup max-height shared across this codebase.
  // After mount, the measured height supersedes this on the next frame.
  estimatedHeight?: number;
}

export interface PopupAnchor {
  open: Accessor<boolean>;
  pos: Accessor<PopupAnchorPos | null>;
  show(): void;
  hide(): void;
  toggle(): void;
  bindTrigger(el: HTMLElement | undefined): void;
  bindPopup(el: HTMLElement | undefined): void;
}

export function usePopupAnchor(opts: UsePopupAnchorOptions = {}): PopupAnchor {
  const [open, setOpen] = createSignal(false);
  const [pos, setPos] = createSignal<PopupAnchorPos | null>(null);
  let triggerEl: HTMLElement | null = null;
  let popupEl: HTMLElement | null = null;

  function updatePos() {
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    const out = flipPopupPositionForElement({
      trigger: triggerEl,
      popup: popupEl,
      width: opts.width ?? r.width,
      estimatedHeight: opts.estimatedHeight,
    });
    // Preserve the original contract: pos.width follows opts.width (or
    // the trigger's width), NOT the measured popup width — Dropdown +
    // SearchableSelect apply this back to the popup style so it sizes
    // to its anchor.
    setPos({ top: out.top, left: out.left, width: opts.width ?? r.width });
  }

  function show() { updatePos(); setOpen(true); }
  function hide() { setOpen(false); }
  function toggle() { if (open()) hide(); else show(); }

  function onOutsideDown(e: Event) {
    const t = e.target as Node;
    if (triggerEl?.contains(t)) return;
    if (popupEl?.contains(t)) return;
    hide();
    // Drop focus from the trigger too — without this the button stays
    // visibly focused (focus ring) after an outside click, even though
    // the popup is gone.
    (triggerEl as HTMLElement | null)?.blur?.();
  }
  function onKey(e: KeyboardEvent) { if (e.key === "Escape") hide(); }
  function onWindowChange() { if (open()) updatePos(); }

  onMount(() => {
    // Listen on pointerdown AND mousedown at capture phase. vis-timeline
    // uses Hammer.js which subscribes to pointer events and can swallow
    // the follow-up mousedown on some pointer paths; pointerdown
    // reliably fires on the document for every primary-button press
    // regardless. Capture phase ensures we see the event even when an
    // inner handler stops bubble-phase propagation.
    document.addEventListener("pointerdown", onOutsideDown, true);
    document.addEventListener("mousedown", onOutsideDown, true);
    window.addEventListener("scroll", onWindowChange, true);
    window.addEventListener("resize", onWindowChange);
    if (opts.closeOnEscape) document.addEventListener("keydown", onKey);
  });
  onCleanup(() => {
    document.removeEventListener("pointerdown", onOutsideDown, true);
    document.removeEventListener("mousedown", onOutsideDown, true);
    window.removeEventListener("scroll", onWindowChange, true);
    window.removeEventListener("resize", onWindowChange);
    if (opts.closeOnEscape) document.removeEventListener("keydown", onKey);
  });

  return {
    open,
    pos,
    show,
    hide,
    toggle,
    bindTrigger(el) { triggerEl = el ?? null; },
    bindPopup(el) {
      popupEl = el ?? null;
      // Re-measure once the popup is in the DOM so updatePos can refine
      // the flip decision with the actual height (vs. the show()-time
      // estimate). rAF defers until layout is committed.
      if (popupEl && open()) requestAnimationFrame(updatePos);
    },
  };
}
